import { downloadBlob } from "../../utils/blob-viewer.util";
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  ViewEncapsulation,
  HostListener,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatDialogModule, MatDialog } from "@angular/material/dialog";
import { MatMenuModule } from "@angular/material/menu";
import { FormsModule } from "@angular/forms";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatCardModule } from "@angular/material/card";
import { AgGridAngular } from "ag-grid-angular";
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  CellClickedEvent,
  GridApi,
  IServerSideDatasource,
  ITextFilterParams,
  IDatasource,
  IGetRowsParams,
  SortModelItem,
  ICellRendererParams,
} from "ag-grid-community";
import { Store } from "@ngrx/store";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { toSignal } from "@angular/core/rxjs-interop";
import { BehaviorSubject, Observable, Subject, take, takeUntil } from "rxjs";

import { DashboardMeasurePatientProd } from "@models/dashboard-measure-patient.model";
import { AddTicketModalComponent } from "@components/modals/add-ticket-modal/add-ticket-modal.component";
import { DashboardService } from "@services/dashboard.service";
import { DashboardFilterRequest } from "@models/dashboard-filter.model";
import { PatientMeasureEvidenceDialogComponent } from "@components/patient-measure-evidence/patient-measure-evidence-dialog.component";
import { PatientValidationConfirmationModalComponent } from "@components/modals/patient-validation-confirmation-modal/patient-validation-confirmation-modal.component";
import { MeasureLogicReportModalComponent } from "@components/modals/measure-logic-report-modal/measure-logic-report-modal.component";
import { myCustomTheme } from "../../constants/grid-themes";
import { PatientActionRendererComponent } from "@components/grid-components/patient-action-renderer/patient-action-renderer.component";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { RoleConstants, ROLE_GROUPS } from "../../constants/roles";
import { MinCharTextFilterComponent } from "@components/shared/min-char-text-filter/min-char-text-filter.component";
import { DropdownFilterComponent } from "@components/shared/dropdown-filter/dropdown-filter.component";

@Component({
  selector: "app-patient-measure-details-prod",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatMenuModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatCardModule,
    AgGridAngular,
    MatSnackBarModule,
  ],
  templateUrl: "./patient-measure-details.component.html",
  styleUrls: ["./patient-measure-details.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PatientMeasureDetailsProdComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly dashboardService = inject(DashboardService);
  private readonly store = inject(Store);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroy$ = new Subject<void>();
  private resizeTimeout: any;

  @Input() measureName: string = "";
  @Input() orgID: string = "";
  @Input() populationNbr: number | null = null;
  @Input() populationCount: number | null = null;
  @Input() filter: DashboardFilterRequest = {
    clinician: ["all"] as string[],
    location: ["all"] as string[],
    measureYear: 2025,
  };

  @Output() close = new EventEmitter<void>();
  @Output() exportStarted = new EventEmitter<void>();
  @Output() exportCompleted = new EventEmitter<void>();

  currentUser = toSignal(this.store.select(selectActiveUser));

  selectedPatient: DashboardMeasurePatientProd | null = null;
  isLoading = false;
  isExporting = false;

  private gridApi!: GridApi;

  readonly theme = myCustomTheme;

  rowData!: DashboardMeasurePatientProd[];

  cacheBlockSize = 100;
  cacheOverflowSize = 2;
  maxConcurrentDatasourceRequests = 2;
  infiniteInitialRowCount = 100;
  maxBlocksInCache = 2;

  readonly gridOptions: GridOptions = {
    pagination: true,
    suppressClickEdit: true,
    rowModelType: "infinite",
    editType: "fullRow",
    onGridReady: this.onGridReady.bind(this),
    onRowClicked: this.onRowClicked.bind(this),
    rowSelection: "single",
    suppressHorizontalScroll: false,
    suppressColumnVirtualisation: true,
    domLayout: "normal",
    getRowStyle: (params) => {
      if (params.node.isSelected()) {
        return { "background-color": "#e3f2fd" };
      }
      return undefined;
    },
    getRowId: (params) => params.data?.encounterID?.toString(),
    context: { componentParent: this },
  };

  readonly defaultColDef: ColDef = {
    flex: 1,
    minWidth: 50,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  };

  readonly frameWorkComponents = {
    patientActionRendererComponent: PatientActionRendererComponent,
    minCharTextFilter: MinCharTextFilterComponent,
    dropdownFilter: DropdownFilterComponent,
  };

  colDefs: ColDef[] = [];

  // A measure with a populationCount is part of a multi-population measure.
  // populationNbr === null identifies the parent/rollup row (no patient-level data of its own).
  // populationNbr > populationCount identifies a virtual/calculated population (also no patient-level data).
  get hasNoPatientData(): boolean {
    if (this.populationCount == null) {
      return false;
    }
    if (this.populationNbr == null) {
      return true;
    }
    return this.populationNbr > this.populationCount;
  }

  ngOnInit(): void {
    this.setColDefs();
    this.store.dispatch(IssueTrackerActions.getTicketCategories());
    this.store.dispatch(IssueTrackerActions.getTicketStatuses());
  }

  isSuperAdminUser(): boolean {
    const user = this.currentUser();
    if (!user) {
      return false;
    }

    return ROLE_GROUPS.SUPER_USER.some(role => user.roles.includes(role));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    if (this.gridApi) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 250);
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  private setColDefs(): void {
    this.colDefs = [
      {
        field: "patientMRN",
        headerName: "Pat MRN",
        flex: 1.2,
        minWidth: 90,
        filter: "minCharTextFilter",
        filterParams: this.getFilterParams(),
        sort: "asc",
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value !== undefined) {
            return this.escapeHtml(params.value);
          } else {
            return '<mat-progress-spinner color="primary" mode="indeterminate"> </mat-progress-spinner> <div class="loading-text">Loading...</div> ';
          }
        },
      },
      {
        field: "patientFirstName",
        headerName: "First Name",
        flex: 1.5,
        minWidth: 110,
        filter: "minCharTextFilter",
        filterParams: this.getFilterParams(),
        tooltipField: "patientFirstName",
        cellStyle: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      {
        field: "patientLastName",
        headerName: "Last Name",
        flex: 1.5,
        minWidth: 110,
        filter: "minCharTextFilter",
        filterParams: this.getFilterParams(),
        tooltipField: "patientLastName",
        cellStyle: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      {
        field: "race1Name",
        headerName: "Race",
        flex: 1,
        minWidth: 80,
        filter: false,
        tooltipField: "race1Name",
        cellStyle: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      {
        field: "patientGender",
        headerName: "Gender",
        flex: 0.8,
        minWidth: 70,
        filter: false, 
        tooltipField: "patientGender",
      },
      {
        field: "age",
        headerName: "Age",
        flex: 0.7,
        minWidth: 60,
        filter: false,
        valueGetter: (params) => {
          if (params.data == undefined) {
            return;
          }
          if (params.data.patientAge != null) {
            return params.data.patientAge;
          }
          const dob = params.data.patientDateOfBirth;
          if (!dob || dob.startsWith('1900')) {
            return null;
          }
          return this.calculateAge(dob);
        },
        valueFormatter: (params) => {
          return params.value != null ? params.value.toString() : "";
        },
        cellStyle: { textAlign: "center" },
      },
      {
        field: "clinician",
        headerName: "Clinician",
        flex: 2.2,
        minWidth: 150,
        filter: "minCharTextFilter",
        filterParams: this.getFilterParams(),
        valueGetter: (params) => {
          if (params.data == undefined) {
            return;
          } else {
            return `${params.data.providerFirstName} ${params.data.providerLastName}`;
          }
        },
        tooltipValueGetter: (params) => {
          if (params.data == undefined) {
            return;
          } else {
            return `${params.data.providerFirstName} ${params.data.providerLastName}`;
          }
        },
        cellStyle: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      {
        field: "serviceLocationName",
        headerName: "Location",
        flex: 1.8,
        minWidth: 130,
        filter: "minCharTextFilter",
        filterParams: this.getFilterParams(),
        tooltipField: "serviceLocationName",
        cellStyle: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      {
        field: "ipp",
        headerName: "IPP",
        flex: 0.6,
        minWidth: 55,
        filter: "dropdownFilter",
        filterParams: this.getFilterParams({ options: ["0", "1"] }),
        cellStyle: { textAlign: "center", fontWeight: "500" },
      },
      {
        field: "denominator",
        headerName: "Den",
        flex: 0.6,
        minWidth: 55,
        filter: "dropdownFilter",
        filterParams: this.getFilterParams({ options: ["0", "1"] }),
        cellStyle: { textAlign: "center", fontWeight: "500" },
      },
      {
        field: "denominatorExclusion",
        headerName: "Den Excl",
        flex: 0.8,
        minWidth: 75,
        filter: "dropdownFilter",
        filterParams: this.getFilterParams({ options: ["0", "1"] }),
        cellStyle: { textAlign: "center", fontWeight: "500" },
      },
      {
        field: "denominatorException",
        headerName: "Den Excep",
        flex: 1.0,
        minWidth: 90,
        filter: "dropdownFilter",
        filterParams: this.getFilterParams({ options: ["0", "1"] }),
        cellStyle: { textAlign: "center", fontWeight: "500" },
      },
      {
        field: "numerator",
        headerName: "Num",
        flex: 0.6,
        minWidth: 55,
        filter: "dropdownFilter",
        filterParams: this.getFilterParams({ options: ["0", "1"] }),
        cellStyle: { textAlign: "center", fontWeight: "500" },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 80,
        filter: false,
        editable: false,
        suppressNavigable: true,
        sortable: true,
        comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
          const validatedA = nodeA.data.validated ? 1 : 0;
          const validatedB = nodeB.data.validated ? 1 : 0;
          return validatedB - validatedA;
        },
        cellRenderer: "patientActionRendererComponent",
        cellRendererParams: (params: any) => {
          if (params.data == undefined) {
            return;
          } else {
            return {
              showCreateTicket: true,
              showValidate: true,
              showInspect: this.isSuperAdminUser(),
              isValidated: params.data.validated,
              validationTooltip: this.getValidationTooltip(params.data),
            };
          }
        },
        cellClass: "action-cell",
      },
    ];
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: (params: IGetRowsParams) => {
        const filter: DashboardFilterRequest = {
          ...this.filter,
          startRow: params.startRow,
          endRow: params.endRow,
          filterModel: params.filterModel,
          sortModel: params.sortModel,
        };

        this.isLoading = true;

        this.dashboardService
          .getMeasurePatients(this.orgID, this.measureName, filter)
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (response: any) => {
              this.isLoading = false;

              let rows: any[] = [];
              let lastRow = -1;

              if (
                response &&
                response.totalCount !== undefined &&
                response.rows
              ) {
                rows = response.rows;
                lastRow = response.totalCount;
              } else {
                console.error("Unexpected response format:", response);
                params.failCallback();
                return;
              }
              params.successCallback(rows, lastRow);
            },
            error: (error) => {
              this.isLoading = false;
              console.error("Error loading patient data:", error);
              params.failCallback();
            },
          });
      },
    };

    setTimeout(() => {
      this.gridOptions.datasource = dataSource;
      params.api!.setGridOption("datasource", dataSource);
      params.api.resetColumnState();
      params.api.sizeColumnsToFit();
    }, 100);
  }

  onRowClicked(event: any): void {
    const patient = event.data as DashboardMeasurePatientProd;
    this.openPatientEvidence(patient);
  }

  onCellClicked(params: CellClickedEvent): void {
    if (params.column?.getColId() === "actions" && params.event?.target) {
      const target = params.event.target as HTMLElement;
      const action = target.dataset["action"];

      if (action === "createTicket") {
        this.createTicket(params.data);
      } else if (action === "validate") {
        this.validatePatient(params.data);
      }
    }
  }

  getFilterParams(additionalParams?: any): any {
    return {
      ...additionalParams,
      isGridLoading: () => this.isLoading,
    };
  }

  private openPatientEvidence(patient: DashboardMeasurePatientProd): void {
    this.selectedPatient = patient;

    // only take the first part of multipart measure names
    let measureCode = this.measureName;
    const re = /([^-]+-\d*)/g;
    const myArray = this.measureName.match(re);
    if (myArray != null && myArray.length == 1) {
      measureCode = myArray[0];
    }
    const dialogRef = this.dialog.open(PatientMeasureEvidenceDialogComponent, {
      panelClass: "full-width-dialog",
      width: "800px",
      maxWidth: "90vw",
      maxHeight: "95vh",
      data: {
        orgID: this.orgID,
        createDate: patient.createDate.replace(/\//g, "_"),
        patientMRN: patient.patientMRN,
        patientID: patient.patientID,
        measureCode: measureCode,
        measureYear: this.filter.measureYear,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.selectedPatient = null;
    });
  }

  createTicket(patient: DashboardMeasurePatientProd): void {
    const dialogRef = this.dialog.open(AddTicketModalComponent, {
      width: "600px",
      data: {
        organizationId: parseInt(this.orgID),
        defaultCategoryName: "Measure Mapping",
        createdFromPatientTable: true,
        lockCategory: true,
        measureCode: this.measureName,
        patientId: patient.patientID,
      },
    });
  }

  openInspectModal(patient: DashboardMeasurePatientProd): void {
    // Extract the base measure code (e.g., "AAO-16" from "AAO-16-PopulationA")
    let measureCode = this.measureName;
    const re = /([^-]+-\d*)/g;
    const myArray = this.measureName.match(re);
    if (myArray != null && myArray.length == 1) {
      measureCode = myArray[0];
    }

    const dialogRef = this.dialog.open(MeasureLogicReportModalComponent, {
      width: "95vw",
      maxWidth: "1400px",
      maxHeight: "95vh",
      panelClass: "measure-logic-modal-container",
      data: {
        organizationId: parseInt(this.orgID),
        patientID: patient.patientID,
        patientMRN: patient.patientMRN,
        measureCode: measureCode,
      },
    });
  }

  validatePatient(patient: DashboardMeasurePatientProd): void {
    if (patient.validated) {
      const dialogRef = this.dialog.open(
        PatientValidationConfirmationModalComponent,
        {
          data: {
            patient: patient,
            action: "remove",
          },
        }
      );
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.removeValidation(patient);
        } else {
          dialogRef.close();
        }
      });
      return;
    }

    const validationRequest = {
      organizationID: parseInt(this.orgID),
      measureCode: this.measureName,
      measureYear: 2025,
      encounterID: patient.encounterID,
      patientID: patient.patientID,
    };

    this.dashboardService
      .validatePatient(validationRequest)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.validated) {
            patient.validated = true;
            patient.validatedDate = new Date().toISOString();
            patient.validatedBy = parseInt(
              this.currentUser()?.loggedInUser?.id ?? "0"
            );
            patient.validationUserName = this.currentUser()?.displayName;

            if (this.gridApi) {
              const rowNode = this.gridApi.getRowNode(
                patient.encounterID.toString()
              );
              if (rowNode) {
                rowNode.setData(patient);
                this.gridApi.refreshCells({
                  rowNodes: [rowNode],
                  columns: ["actions"],
                  force: true,
                });
              } else {
                this.refreshGridData();
              }
            }
          } else {
            alert("Failed to validate patient");
          }
        },
        error: (err) => {
          console.error("Error validating patient:", err);
          alert("Error validating patient. Please try again.");
        },
      });
  }

  private performRemoveValidation(patient: DashboardMeasurePatientProd): void {
    const validationRequest = {
      measureCode: this.measureName,
      encounterID: patient.encounterID,
    };

    this.dashboardService
      .removePatientValidation(validationRequest)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.removed) {
            patient.validated = false;
            patient.validatedDate = undefined;
            patient.validatedBy = undefined;
            patient.validationUserName = undefined;

            if (this.gridApi) {
              const rowNode = this.gridApi.getRowNode(
                patient.encounterID.toString()
              );
              if (rowNode) {
                rowNode.setData(patient);
                this.gridApi.refreshCells({
                  rowNodes: [rowNode],
                  columns: ["actions"],
                  force: true,
                });
              } else {
                this.refreshGridData();
              }
            }
          } else {
            alert("Failed to remove patient validation");
          }
        },
        error: (err) => {
          console.error("Error removing patient validation:", err);
          alert("Error removing validation. Please try again.");
        },
      });
  }

  removeValidation(patient: DashboardMeasurePatientProd): void {
    this.performRemoveValidation(patient);
  }

  private refreshGridData(): void {
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  exportPatientsCSV(): void {
    if (this.hasNoPatientData) {
      alert("This measure has no patient data to export.");
      return;
    }

    if (!this.gridApi) {
      alert("Grid not initialized");
      return;
    }

    if (this.isLoading) {
      alert("Loading Data, Please Wait");
      return;
    }

    if (this.isExporting) {
      this.snackBar.open("Export already in progress", "CLOSE", {
        duration: 3000,
      });
      return;
    }

    this.isLoading = true;
    this.isExporting = true;

    this.exportStarted.emit();

    const snackBarRef = this.snackBar.open(
      "Exporting data... This may take a few minutes.",
      "DISMISS",
      { duration: 0, horizontalPosition: "center", verticalPosition: "bottom" }
    );

    const filterModel = this.gridApi.getFilterModel();
    const columnState = this.gridApi.getColumnState();
    const sortModel: SortModelItem[] = columnState
      .filter((col) => col.sort === "asc" || col.sort === "desc")
      .map((col) => ({
        colId: col.colId,
        sort: col.sort as "asc" | "desc",
      }));

    const exportFilter: DashboardFilterRequest = {
      clinician: this.filter.clinician,
      location: this.filter.location,
      measureYear: this.filter.measureYear,
      filterModel: filterModel,
      sortModel: sortModel,
    };

    this.dashboardService
      .getMeasurePatients(this.orgID, this.measureName, exportFilter)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (allData: any) => {
          this.isLoading = false;
          this.isExporting = false;
          this.exportCompleted.emit();
          snackBarRef.dismiss();

          let dataArray: DashboardMeasurePatientProd[] = [];

          if (Array.isArray(allData)) {
            dataArray = allData;
          } else if (allData.rows) {
            dataArray = allData.rows;
          } else if (allData.data) {
            dataArray = allData.data;
          }

          if (dataArray.length === 0) {
            alert("No data available to export");
            return;
          }

          const exportData = dataArray.map(
            (patient: DashboardMeasurePatientProd) => ({
              patientMRN: patient.patientMRN || "",
              patientFirstName: patient.patientFirstName || "",
              patientLastName: patient.patientLastName || "",
              race1Name: patient.race1Name,
              patientGender: patient.patientGender || "",
              age: patient.patientAge != null
                ? patient.patientAge
                : (!patient.patientDateOfBirth || patient.patientDateOfBirth.startsWith('1900')
                  ? ""
                  : this.calculateAge(patient.patientDateOfBirth)),
              clinician: `${patient.providerFirstName || ""} ${
                patient.providerLastName || ""
              }`.trim(),
              serviceLocationName: patient.serviceLocationName || "",
              encounterDate: patient.encounterDate || "",
              ipp: patient.ipp || 0,
              denominator: patient.denominator || 0,
              denominatorExclusion: patient.denominatorExclusion || 0,
              denominatorException: patient.denominatorException || 0,
              numerator: patient.numerator || 0,
              validated: patient.validated ? "Yes" : "No",
            })
          );

          this.generateAndDownloadCSV(exportData);

          this.snackBar.open(
            `Successfully exported ${dataArray.length} records!`,
            "CLOSE",
            { duration: 5000 }
          );
        },
        error: (error) => {
          this.isLoading = false;
          this.isExporting = false;
          this.exportCompleted.emit();
          snackBarRef.dismiss();

          this.snackBar.open("Export failed. Please try again.", "CLOSE", {
            duration: 5000,
            panelClass: ["error-snackbar"],
          });
        },
      });
  }

  private generateAndDownloadCSV(data: any[]): void {
    if (data.length === 0) return;

    const headers = [
      "Pat MRN",
      "First Name",
      "Last Name",
      "Race",
      "Gender",
      "Age",
      "Clinician",
      "Location",
      "Encounter Date",
      "IPP",
      "Den",
      "Den Excl",
      "Den Excep",
      "Num",
      "Validated",
    ];

    const escapeCSVValue = (value: any): string => {
      if (value === null || value === undefined) return "";
      const stringValue = value.toString();
      if (
        stringValue.includes(",") ||
        stringValue.includes('"') ||
        stringValue.includes("\n")
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvRows = data.map((row) =>
      [
        row.patientMRN,
        row.patientFirstName,
        row.patientLastName,
        row.race1Name,
        row.patientGender,
        row.age,
        row.clinician,
        row.serviceLocationName,
        row.encounterDate,
        row.ipp,
        row.denominator,
        row.denominatorExclusion,
        row.denominatorException,
        row.numerator,
        row.validated,
      ]
        .map(escapeCSVValue)
        .join(",")
    );

    const csvContent = [headers.join(","), ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `${this.measureName}_MeasurePatients_${timestamp}.csv`;

    downloadBlob(blob, filename);
  }

  calculateAge(dateOfBirth: string): number {
    if (!dateOfBirth) return 0;

    try {
      const now = new Date();
      const dob = new Date(dateOfBirth);

      if (isNaN(dob.getTime())) {
        console.warn("Invalid date format:", dateOfBirth);
        return 0;
      }
      let age = now.getFullYear() - dob.getFullYear();
      const monthDiff = now.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
        age--;
      }

      return Math.max(0, age);
    } catch (error) {
      return 0;
    }
  }

  getValidationTooltip(patient: DashboardMeasurePatientProd): string {
    if (!patient.validated) {
      return "";
    }

    const userName =
      patient.validationUserName || patient.validatedBy || "Unknown User";
    const dateString = patient.validatedDate
      ? this.formatValidationDate(patient.validatedDate)
      : "Unknown Date";

    return `Validated by ${userName} on ${dateString}`;
  }

  formatValidationDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  }

  onClose(): void {
    this.close.emit();
  }
}
